import * as React from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Typography
} from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useCallback, useEffect, useRef, useState } from "react";
import { t } from "../../../components/translate";
import useAssessmentLabel from "../../../hooks/useAssessmentLabel";
import useAudioRecorder from "../../../hooks/useAudioRecorder";
import useHasTranscriptionEnabled from "../../../hooks/useHasTranscriptionEnabled";
import usePrescriptionSummary from "../../../hooks/usePrescriptionSummary";
import useSpeechToText from "../../../hooks/useSpeechToText";
import { formatPrescriptionSummaryText } from "../../../helpers/prescriptionSummaryFormat";
import styles from "../Assessment.module.css";
import RecordingWaveform from "./RecordingWaveform";
import { formatToHourMinuteSecondString } from "../../../hooks/useElapsedTime";

interface Props {
  value: string | null;
  onChange: (value: string | null) => void;
  transcript?: string;
  onTranscriptChange?: (value: string) => void;
  summary?: string;
  onSummaryChange?: (value: string) => void;
  transcriptionEnabled?: boolean;
}

enum MAX_RECORD_TIME {
  SECOND = 180,
  MINUTE = 3
}

const RecordAudio = ({
  value,
  onChange,
  transcript = "",
  onTranscriptChange,
  summary = "",
  onSummaryChange,
  transcriptionEnabled = false
}: Props): JSX.Element => {
  const assessmentLabels = useAssessmentLabel();
  const { isOkhatiAiConfigured } = useHasTranscriptionEnabled();
  const {
    summarize,
    isSummarizing,
    error: summaryError,
    isDeepSeekReady
  } = usePrescriptionSummary();
  const [permissionError, setPermissionError] = useState<Error | null>(null);
  const [blobURI, setBlobURI] = useState<string | null>(value);
  const [recordTime, setRecordTime] = useState(0);
  const [localTranscript, setLocalTranscript] = useState(transcript);
  const [localSummary, setLocalSummary] = useState(summary);
  const [summaryGenerationFailed, setSummaryGenerationFailed] = useState(false);
  const [showTranscriptView, setShowTranscriptView] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const latestTranscriptRef = useRef(transcript);
  const finalizedTranscriptRef = useRef(transcript);
  const transcriptUsedForSummaryRef = useRef(transcript);
  const isTranscriptLockedRef = useRef(Boolean(transcript?.trim()));
  const latestSummaryRef = useRef(summary);
  const onChangeRef = useRef(onChange);
  const onTranscriptChangeRef = useRef(onTranscriptChange);
  const onSummaryChangeRef = useRef(onSummaryChange);
  const blobUrlRef = useRef<string | null>(null);
  const isDeletedRef = useRef(false);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    onTranscriptChangeRef.current = onTranscriptChange;
  }, [onTranscriptChange]);

  useEffect(() => {
    onSummaryChangeRef.current = onSummaryChange;
  }, [onSummaryChange]);

  const revokeBlobUrl = useCallback((url: string | null) => {
    if (url?.startsWith("blob:")) {
      window.URL.revokeObjectURL(url);
    }
  }, []);

  const {
    startRecording,
    isRecording,
    stopRecording,
    recordingTime,
    isPaused,
    recordingBlob,
    togglePauseResume
  } = useAudioRecorder({
    onNotAllowedOrFound: (exception) => {
      const message = exception instanceof Error ? exception.message : String(exception);
      setPermissionError(new Error(message));
    },
    mediaRecorderOptions: { mimeType: "audio/webm" }
  });

  const handleTranscript = useCallback((text: string) => {
    try {
      if (isTranscriptLockedRef.current) return;

      latestTranscriptRef.current = text;
      setLocalTranscript(text);
    } catch {
      // Parent state updates must not crash recording UI.
    }
  }, []);

  const { status, error, start, stop, pause, resume, getTranscript, isActive, analyser } =
    useSpeechToText({
      onTranscript: handleTranscript
    });

  const onTogglePauseResume = () => {
    if (transcriptionEnabled) {
      if (isPaused) {
        resume();
      } else {
        pause();
      }
    }
    togglePauseResume();
  };

  const clearSummary = useCallback(() => {
    setLocalSummary("");
    latestSummaryRef.current = "";
    onSummaryChangeRef.current?.("");
    setSummaryGenerationFailed(false);
    setShowTranscriptView(false);
  }, []);

  const onRecordingStart = async () => {
    if (isStarting || isActive || isRecording) return;

    isDeletedRef.current = false;
    setIsStarting(true);
    setRecordTime(0);
    setBlobURI(null);
    setPermissionError(null);
    setLocalTranscript("");
    latestTranscriptRef.current = "";
    finalizedTranscriptRef.current = "";
    transcriptUsedForSummaryRef.current = "";
    isTranscriptLockedRef.current = false;
    onTranscriptChangeRef.current?.("");
    clearSummary();

    try {
      if (transcriptionEnabled) {
        const started = await start();
        if (!started) return;

        const recorded = await startRecording();
        if (!recorded) {
          stop();
        }
      } else {
        await startRecording();
      }
    } catch {
      setPermissionError(
        new Error(
          transcriptionEnabled ? "Failed to start voice transcription" : "Failed to start recording"
        )
      );
    } finally {
      setIsStarting(false);
    }
  };

  const persistTranscript = useCallback((text: string) => {
    const normalized = text.trim();
    if (!normalized) return;

    isTranscriptLockedRef.current = true;
    finalizedTranscriptRef.current = normalized;
    latestTranscriptRef.current = normalized;
    setLocalTranscript(normalized);
    onTranscriptChangeRef.current?.(normalized);
  }, []);

  const onRecordingComplete = useCallback(async () => {
    setRecordTime(recordingTime);

    if (transcriptionEnabled) {
      const finalizedTranscript = (
        getTranscript() ||
        latestTranscriptRef.current ||
        finalizedTranscriptRef.current
      ).trim();

      if (finalizedTranscript) {
        transcriptUsedForSummaryRef.current = finalizedTranscript;
        persistTranscript(finalizedTranscript);
      }

      stop();
    }
    stopRecording();

    const transcriptForSummary = transcriptUsedForSummaryRef.current.trim();

    if (!transcriptionEnabled || !transcriptForSummary || !isDeepSeekReady) {
      return;
    }

    setSummaryGenerationFailed(false);

    try {
      const result = await summarize(transcriptForSummary);
      const formattedResult = formatPrescriptionSummaryText(result);
      setLocalSummary(formattedResult);
      latestSummaryRef.current = formattedResult;
      onSummaryChangeRef.current?.(formattedResult);
      setShowTranscriptView(false);
    } catch {
      setSummaryGenerationFailed(true);
    }
  }, [
    recordingTime,
    transcriptionEnabled,
    getTranscript,
    persistTranscript,
    stop,
    stopRecording,
    isDeepSeekReady,
    summarize
  ]);

  const onTranscriptionTextDelete = () => {
    setLocalTranscript("");
    latestTranscriptRef.current = "";
    finalizedTranscriptRef.current = "";
    transcriptUsedForSummaryRef.current = "";
    isTranscriptLockedRef.current = false;
    onTranscriptChangeRef.current?.("");
    clearSummary();
    setShowTranscriptView(false);
  };

  const onRecordingDelete = () => {
    isDeletedRef.current = true;
    revokeBlobUrl(blobUrlRef.current);
    blobUrlRef.current = null;
    setBlobURI(null);
    setRecordTime(0);
    setLocalTranscript("");
    latestTranscriptRef.current = "";
    finalizedTranscriptRef.current = "";
    transcriptUsedForSummaryRef.current = "";
    isTranscriptLockedRef.current = false;
    onTranscriptChangeRef.current?.("");
    clearSummary();
    onChangeRef.current(null);
    if (transcriptionEnabled) {
      stop();
    }
    if (isRecording) {
      stopRecording();
    }
  };

  const isBusy = isStarting;
  const isListening = isRecording || (transcriptionEnabled && status === "listening");
  const effectiveSummary = formatPrescriptionSummaryText(localSummary || summary);
  const effectiveTranscript =
    localTranscript ||
    transcriptUsedForSummaryRef.current ||
    finalizedTranscriptRef.current ||
    latestTranscriptRef.current ||
    transcript;
  const shouldShowSummary =
    transcriptionEnabled &&
    !isListening &&
    Boolean(effectiveSummary) &&
    !summaryGenerationFailed &&
    !showTranscriptView;
  const shouldShowTranscript =
    transcriptionEnabled &&
    (isListening || isSummarizing || showTranscriptView || !shouldShowSummary);

  useEffect(() => {
    setBlobURI(value);
  }, [value]);

  useEffect(() => {
    if (isListening || isTranscriptLockedRef.current) return;
    if (!transcript?.trim()) return;

    const localTranscriptValue = finalizedTranscriptRef.current.trim();
    if (localTranscriptValue.length > transcript.trim().length) return;

    setLocalTranscript(transcript);
    latestTranscriptRef.current = transcript;
    finalizedTranscriptRef.current = transcript;
    transcriptUsedForSummaryRef.current = transcript;
  }, [transcript, isListening]);

  useEffect(() => {
    if (!isListening && summary) {
      setLocalSummary(summary);
      latestSummaryRef.current = summary;
    }
  }, [summary, isListening]);

  useEffect(() => {
    if (!recordingBlob || isDeletedRef.current) return;

    revokeBlobUrl(blobUrlRef.current);
    const URI = window.URL.createObjectURL(new Blob([recordingBlob]));
    blobUrlRef.current = URI;
    setBlobURI(URI);
    onChangeRef.current(URI);
  }, [recordingBlob, revokeBlobUrl]);

  useEffect(
    () => () => {
      revokeBlobUrl(blobUrlRef.current);
    },
    [revokeBlobUrl]
  );

  useEffect(() => {
    if (recordingTime >= MAX_RECORD_TIME.SECOND) {
      onRecordingComplete().catch(() => undefined);
    }
  }, [recordingTime, onRecordingComplete]);

  const transcriptPlaceholder = (() => {
    if (isPaused) return "Paused";
    if (isListening) return "Listening... speak your prescription";
    return "Transcribed prescription will appear here";
  })();

  const handlePrimaryAction = () => {
    if (!isListening) {
      if (shouldShowSummary) {
        const transcriptToShow =
          transcriptUsedForSummaryRef.current ||
          finalizedTranscriptRef.current ||
          localTranscript ||
          transcript;
        if (transcriptToShow.trim()) {
          persistTranscript(transcriptToShow);
        }
        setShowTranscriptView(true);
        return;
      }
      if (showTranscriptView && effectiveSummary) {
        setShowTranscriptView(false);
        return;
      }
      onRecordingStart().catch(() => undefined);
      return;
    }
    if (isPaused) {
      onTogglePauseResume();
    } else {
      onRecordingComplete().catch(() => undefined);
    }
  };

  const primaryLabel = (() => {
    if (isListening) return isPaused ? t("resume") : t("stop");
    if (shouldShowSummary) return t("seeTranscribedText");
    if (showTranscriptView && effectiveSummary) return t("seeSummary");
    return t("startTranscribing");
  })();

  const controlButtonSx = {
    backgroundColor: "#F1F3F4",
    color: "text.primary",
    borderRadius: "6px",
    textTransform: "none" as const,
    fontWeight: 500,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#E8EAED",
      boxShadow: "none"
    },
    "&.Mui-disabled": {
      backgroundColor: "#F1F3F4",
      color: "text.disabled"
    }
  };

  const hasRecordingContent = Boolean(
    value ||
      blobURI ||
      transcript ||
      localTranscript ||
      finalizedTranscriptRef.current ||
      latestTranscriptRef.current ||
      summary ||
      localSummary
  );
  const hasTranscriptionText = Boolean(
    transcript ||
      localTranscript ||
      finalizedTranscriptRef.current ||
      latestTranscriptRef.current ||
      summary ||
      localSummary
  );
  const isMenuOpen = Boolean(menuAnchor);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handlePauseFromMenu = () => {
    handleMenuClose();
    if (isListening && !isPaused) {
      onTogglePauseResume();
    }
  };

  const handleResumeFromMenu = () => {
    handleMenuClose();
    if (isListening && isPaused) {
      onTogglePauseResume();
    }
  };

  const handleDeleteFromMenu = () => {
    handleMenuClose();
    onRecordingDelete();
  };

  const handleDeleteTranscriptionFromMenu = () => {
    handleMenuClose();
    onTranscriptionTextDelete();
  };

  const isMicrophoneError = (err: Error): boolean =>
    err.name === "NotAllowedError" ||
    err.name === "NotFoundError" ||
    /permission|microphone|denied/i.test(err.message);

  const recordingIndicator = (
    <Box
      sx={{
        height: "0.5rem",
        width: "0.5rem",
        borderRadius: "50%",
        background: "error.main",
        flexShrink: 0
      }}
    />
  );

  return (
    <div>
      <Box className={styles.assessmentRow} height="auto" alignItems="flex-start">
        <Box className={styles.assessmentLabel} pt="4px">
          <Typography component="span" fontWeight="600">
            {assessmentLabels.recordPrescription}
          </Typography>
        </Box>
        <Box className={styles.assessmentField}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              minHeight: "2rem"
            }}
          >
            <Box
              sx={{ display: "flex", gap: 1, alignItems: "center", flex: 1, minWidth: 0, mr: 2 }}
            >
              {isListening && (
                <RecordingWaveform
                  active={!isPaused}
                  analyser={transcriptionEnabled ? analyser : null}
                />
              )}
              {!isListening && blobURI && (
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <Typography variant="body2" color="text.secondary">
                    {formatToHourMinuteSecondString(recordTime, true)}
                  </Typography>
                  <Typography variant="body2">{t("done")}</Typography>
                </Box>
              )}
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                flexShrink: 0
              }}
            >
              <IconButton
                size="small"
                onClick={handleMenuOpen}
                aria-label="Transcription options"
                sx={{
                  ...controlButtonSx,
                  width: 32,
                  height: 32,
                  color: "text.secondary"
                }}
              >
                <TuneIcon sx={{ fontSize: "1rem" }} />
              </IconButton>
              <Menu
                anchorEl={menuAnchor}
                open={isMenuOpen}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                {isListening && !isPaused && (
                  <MenuItem onClick={handlePauseFromMenu} sx={{ gap: 1.5, py: 1.25 }}>
                    {recordingIndicator}
                    <Typography variant="body2">{t("pauseTranscription")}</Typography>
                  </MenuItem>
                )}
                {isListening && isPaused && (
                  <MenuItem onClick={handleResumeFromMenu} sx={{ gap: 1.5, py: 1.25 }}>
                    {recordingIndicator}
                    <Typography variant="body2">{t("resumeTranscription")}</Typography>
                  </MenuItem>
                )}
                {isListening && hasRecordingContent && <Divider />}
                <MenuItem
                  onClick={handleDeleteFromMenu}
                  disabled={!hasRecordingContent}
                  sx={{ gap: 1.5, py: 1.25 }}
                >
                  <DeleteOutlineIcon fontSize="small" sx={{ color: "error.main" }} />
                  <Typography variant="body2" color="error">
                    {t("deleteRecording")}
                  </Typography>
                </MenuItem>
                {transcriptionEnabled && (
                  <MenuItem
                    onClick={handleDeleteTranscriptionFromMenu}
                    disabled={!hasTranscriptionText}
                    sx={{ gap: 1.5, py: 1.25 }}
                  >
                    <DeleteOutlineIcon fontSize="small" sx={{ color: "error.main" }} />
                    <Typography variant="body2" color="error">
                      {t("deleteTranscriptionText")}
                    </Typography>
                  </MenuItem>
                )}
              </Menu>
              <Button
                variant="contained"
                disableElevation
                onClick={handlePrimaryAction}
                disabled={
                  isBusy || isSummarizing || (transcriptionEnabled && !isOkhatiAiConfigured)
                }
                sx={{
                  ...controlButtonSx,
                  px: 1.5,
                  py: 0.75,
                  minHeight: 32
                }}
              >
                {primaryLabel}
              </Button>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              py: 1,
              px: 2
            }}
          >
            {transcriptionEnabled && !isOkhatiAiConfigured && (
              <Alert severity="warning">
                <AlertTitle>Transcription not configured</AlertTitle>
                {t("okhatiAiNotConfigured")}
              </Alert>
            )}
            {permissionError && (
              <Alert severity="error">
                <AlertTitle>Error</AlertTitle>
                {permissionError.message}
                {isMicrophoneError(permissionError) && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {t("allowMicrophone")}
                  </Typography>
                )}
              </Alert>
            )}
            {transcriptionEnabled && error && !permissionError && (
              <Alert severity="error">
                <AlertTitle>Transcription error</AlertTitle>
                {error}
              </Alert>
            )}
            {summaryGenerationFailed && summaryError && (
              <Alert severity="warning">
                <AlertTitle>{t("summaryGenerationError")}</AlertTitle>
                {summaryError}
              </Alert>
            )}
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            {!!blobURI && <audio controls src={blobURI} />}
            {isSummarizing && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  {t("generatingSummary")}
                </Typography>
              </Box>
            )}
            {shouldShowTranscript && (
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Prescription text"
                placeholder={transcriptPlaceholder}
                value={effectiveTranscript}
                onChange={(e) => {
                  const nextValue = e.target.value;
                  setLocalTranscript(nextValue);
                  latestTranscriptRef.current = nextValue;
                  finalizedTranscriptRef.current = nextValue;
                }}
                onBlur={() => {
                  onTranscriptChangeRef.current?.(finalizedTranscriptRef.current);
                }}
                slotProps={{
                  inputLabel: { shrink: true }
                }}
              />
            )}
            {shouldShowSummary && (
              <TextField
                fullWidth
                multiline
                minRows={6}
                maxRows={12}
                label={t("summaryPrescription")}
                placeholder={t("summaryPrescriptionPlaceholder")}
                value={effectiveSummary}
                onChange={(e) => {
                  const nextValue = e.target.value;
                  setLocalSummary(nextValue);
                  latestSummaryRef.current = nextValue;
                }}
                onBlur={() => {
                  onSummaryChangeRef.current?.(latestSummaryRef.current);
                }}
                slotProps={{
                  inputLabel: { shrink: true }
                }}
              />
            )}
            {recordTime >= MAX_RECORD_TIME.SECOND && (
              <Alert severity="info">{`Maximum recording time is ${MAX_RECORD_TIME.MINUTE} minutes.`}</Alert>
            )}
          </Box>
        </Box>
      </Box>
    </div>
  );
};

export default RecordAudio;
